<template>
  <Loader
    :value="promise"
    :icon="['fal', 'spinner-third']"
    :message="$t('loading', { resource: $tc('addonShop').toLowerCase() }) + '...'"
    theme="default"
    :class="['shop', 'shop--theme-' + theme]"
    :data-step="stepSlug"
    :data-subcategory-slug="subcategorySlug"
  >
    <template v-if="data.id">
      <Steps v-model="step" :items="categories" :summary="summary" :sets="hasSets" />

      <CostBreakdown :shop="data" :parts="costParts" />

      <ProductDetails
        v-if="category"
        :value="product"
        :category="category"
        :subcategory="subcategory"
        @product:details="
          product => (productModal = { product, props: { showFloorplan: false, readonly: true } })
        "
        @select="({ id, variant }) => select(id, null, variant)"
      />

      <Navigation :hasNext="hasNext" :hasPrev="hasPrev" @next="next" @prev="prev">
        <template #action>
          <slot name="action" />
        </template>
      </Navigation>

      <Summary v-if="step === 'summary'" :shop="data" @prev="prev">
        <template v-for="slot of ['summary']" v-slot:[slot]="scope"
          ><slot :name="slot" v-bind="{ ...scope, next, prev }"
        /></template>
      </Summary>

      <Intro v-if="step === 'intro'" :value="data" @next="next">
        <template v-for="slot of ['contact', 'duedates', 'actions']" v-slot:[slot]="scope"
          ><slot :name="slot" v-bind="{ ...scope, next, prev }"
        /></template>
      </Intro>

      <Sets
        v-if="step === 'sets'"
        :value="data"
        @select="selectSet"
        @product:details="
          product => (productModal = { product, props: { showFloorplan: false, readonly: true } })
        "
      />

      <Subcategories
        v-model="subcategory"
        :category="category"
        @open:modal="$ev => (productModal = $ev)"
        @select="select"
        @unselect="$ev => unselect($ev, false, { save: true })"
      />

      <ProductModal @select="({ id, variant }) => select(id, null, variant)" />
    </template>
  </Loader>
</template>

<script>
import Vue from 'vue'
import { LoaderComponent as Loader } from 'vue-elder-loader'
import Steps from './Steps'
import Subcategories from './Subcategories/index'
import Intro from './Intro'
import Summary from './Summary/index'
import ProductDetails from './Product/Details'
import ProductModal from './Product/Modal'
import CostBreakdown from './Summary/CostBreakdown.vue'
import Navigation from './Navigation.vue'
import Sets from './Sets'

import {
  CheckConditions,
  getConditionalDependencies,
  ConditionFilter,
  PromiseQueue,
  getSelectedItems,
  Base64Util,
  getItem,
  convertSelectionToBase64,
  ResetShop,
} from './utils'

import { getShop, selectItem, unselectItem } from './api'

const selectionQueue = PromiseQueue()

const selectItemQueued = (...args) => selectionQueue(() => selectItem(...args))
const unselectItemQueued = (...args) => selectionQueue(() => unselectItem(...args))

export default {
  props: {
    id: String,
    intro: {
      type: Boolean,
      default: true,
    },
    summary: {
      type: Boolean,
      default: true,
    },
    theme: {
      type: String,
      default: 'light',
    },
  },
  provide() {
    const val = {}

    Object.defineProperties(val, {
      getItemStatus: { get: () => this.getItemStatus },
      shop: { get: () => this.data },
      category: { get: () => this.category },
      subcategory: { get: () => this.subcategory },
      sets: { get: () => this.sets },
      bus: { get: () => this.bus },
      accessToken: { get: () => this.accessToken },
    })

    return val
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
    category() {
      this.setSubcategory()
      this.$nextTick(() => {
        const target = this.$el.querySelector('.shop-subcategories')
        if (target) target.scrollTo(0, 0)
      })
    },
    step: {
      handler(val) {
        this.$emit('navigate', val)

        if (this.$router && (!this.$route.query.step || this.$route.query.step !== val))
          this.$router.replace({ query: { ...this.$route.query, step: val } })
      },
    },
  },
  data() {
    return {
      productModal: null,
      promise: null,
      data: {},
      step: 'intro',
      subcategory: null,
      bus: new Vue(),
    }
  },
  computed: {
    subcategorySlug() {
      if (!this.subcategory) return ''
      return this.slugify(this.subcategory.label)
    },
    accessToken() {
      let params = new URLSearchParams(window.location.search)
      return params.get('accessToken')
    },
    costParts() {
      if (!this.$path('data.reference.sale.value') & !this.$path('data.reference.price'))
        return ['total']
      return ['shop', 'unit', 'total']
    },
    stepSlug() {
      switch (this.step) {
        case 'intro':
        case 'summary':
        case 'sets':
          return this.step
        default:
          return 'category'
      }
    },
    category: {
      get() {
        if (!this.data.categories) return
        return this.data.categories.find(c => c.id === this.step)
      },
      set(val) {
        return (this.step = val.id)
      },
    },
    hasSets() {
      return Boolean(this.data.sets && this.data.sets.length)
    },
    floorplan() {
      if (!this.$path('category.floorplans.length')) return
      return this.category.floorplans.filter(f => CheckConditions(f, this.data)).find(Boolean)
    },
    product() {
      if (!this.subcategory) return
      return (this.subcategory.items || []).find(item => item.selected)
    },
    categories() {
      return this.data.categories.filter(c => ConditionFilter(c, this.data))
    },
    hasNext() {
      return Boolean(this.next(true))
    },
    hasPrev() {
      return Boolean(this.prev(true))
    },
    sets() {
      if (!this.$path('data.sets.length')) return

      const getSetItems = set => {
        return this.data.categories.reduce((productLinks, category) => {
          category.categories.forEach(subcategory => {
            subcategory.items
              .filter(i => (i.sets || []).includes(set))
              .forEach(link => productLinks.push(link))
          })

          return productLinks
        }, [])
      }

      return this.data.sets.map(set => {
        let items = getSetItems(set.id)

        let selected = getSelectedItems(this.data).filter(s => s.sets.includes(set.id))

        return {
          ...set,
          score: selected.length / items.length,
        }
      })
    },
  },
  methods: {
    slugify(v) {
      return v
        .toString()
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
    },
    getItem(id) {
      return getItem(id, this.data)
    },
    next(dry, options = {}) {
      if (options.navigateTo) this.step = options.navigateTo
      if (this.step === 'intro' && this.hasSets) {
        if (!dry) this.step = 'sets'
        return true
      }

      let catindex = this.categories.findIndex(c => c === this.category)
      let nextcat = this.categories[catindex + 1]

      if (nextcat) {
        if (!dry) this.category = nextcat
        return true
      }

      if (this.summary && this.category) {
        if (!dry) this.step = 'summary'
        return true
      }

      return false
    },
    prev(dry) {
      if (!this.category) {
        if (!dry) this.category = this.categories[this.categories.length - 1]
        return true
      }

      let catindex = this.categories.findIndex(c => c === this.category)
      let prevcat = this.categories[catindex - 1]

      if (prevcat) {
        if (!dry) this.category = prevcat
        return true
      }

      if (this.hasSets) {
        if (!dry) this.step = 'sets'
        return true
      }

      if (this.stepSlug === 'category' && catindex === 0) {
        if (!dry) this.step = 'intro'
        return true
      }

      return false
    },
    setSubcategory(subcategory) {
      if (!this.category) return (this.subcategory = null)
      this.subcategory =
        subcategory ||
        this.category.categories.filter(category => ConditionFilter(category, this.data)).at(0)
    },
    fetch() {
      if (!this.id) return

      const params = new URLSearchParams(window.location.search)

      this.promise = getShop({ id: this.id, customSalesman: params.has('salesman') }).then(data => {
        if (this.accessToken) {
          this.data = data
        } else {
          this.data = ResetShop(data)

          if (params.has('selections'))
            (Base64Util.from(params.get('selections')) || []).forEach(({ id, quantity, variant }) =>
              this.select(id, quantity, variant),
            )
        }

        if (this.$route.query.step) this.step = this.$route.query.step

        return this.data
      })

      return this.promise
    },
    getItemStatus(id) {
      let defaultState = { isSelectable: true, isUnselectable: true, dependencies: [] }
      if (!id) return defaultState

      let { item, subcategory, category } = this.getItem(id)

      if (!item) return defaultState

      let lockedStatuses = ['accepted', 'signed', 'expired']
      let dependencies = getConditionalDependencies(id, this.data)

      let selectConditions = [
        CheckConditions(category, this.data),
        CheckConditions(subcategory, this.data),
        CheckConditions(item, this.data),
        !lockedStatuses.includes(category.status),
        dependencies.ifnot.every(i => {
          let { category } = this.getItem(i)
          return !category || !lockedStatuses.includes(category.status)
        }),
      ]

      let unselectConditions = [
        !lockedStatuses.includes(category.status),
        dependencies.if.every(i => {
          let { category } = this.getItem(i)
          return !category || !lockedStatuses.includes(category.status)
        }),
      ]

      return {
        isSelectable: selectConditions.every(Boolean),
        isUnselectable: unselectConditions.every(Boolean),
        dependencies,
      }
    },
    select(id, quantity, variantId) {
      let { subcategory, item } = this.getItem(id)

      if (!item) return

      let variant
      if (variantId) {
        const { category, items = [] } = item.product.variants || {}

        variant = {
          category,
          value: items.find(({ internalId }) => internalId === variantId),
        }
      }

      let { isSelectable, dependencies = {} } = this.getItemStatus(id)

      this.setSubcategory(subcategory)

      if (!isSelectable) return false

      // Unselect all conditionals
      let depOperations = dependencies.ifnot.map(i => this.unselect(i, true))
      if (!depOperations.every(Boolean)) return false

      // Perform selection
      let itemsOperations = subcategory.items.map(i => {
        if (i !== item) return this.unselect(i.id)
        return true
      })
      if (!itemsOperations.every(Boolean)) return false

      if (
        item.selected &&
        (!quantity || (quantity && item.quantity.value === quantity)) &&
        variant &&
        item.variant &&
        item.variant.value === variant.value.internalId
      )
        return true

      item.selected = true
      if (quantity) item.quantity.value = quantity
      if (variant && variant.value) {
        this.$set(item, 'variant', {
          value: variant.value.internalId,
          category: variant.category,
        })
      }

      // Send to server
      if (this.accessToken) {
        let payload = { accessToken: this.accessToken }
        if (item.quantity.enabled) payload.quantity = item.quantity.value || item.quantity.min
        if (variant) payload.variant = item.variant
        selectItemQueued(id, payload)
      } else {
        this.saveSelectionState()
      }

      return true
    },
    unselect(id, setDefault, options = {}) {
      let { save = false } = options

      let { subcategory, item } = this.getItem(id)
      if (!subcategory || !item.selected) return true

      let { isUnselectable, dependencies } = this.getItemStatus(id)

      if (!isUnselectable) return false

      // Unselect all conditionals
      let operations = dependencies.if.map(i => this.unselect(i, true))
      if (!operations.every(Boolean)) return false

      // Perform unselection
      item.selected = false
      if (setDefault)
        subcategory.items.forEach(i => {
          if (!i.isDefault) return
          i.selected = true
        })

      // Send to server
      if (this.accessToken && save) unselectItemQueued(id, { accessToken: this.accessToken })
      else this.saveSelectionState()

      return true
    },
    selectSet(ids) {
      if (ids) ids.forEach(id => this.select(id))
      return (this.category = this.data.categories[0])
    },
    saveSelectionState() {
      const selections = convertSelectionToBase64(this.data)
      if (this.$route.query.selections !== selections)
        this.$router.replace({ query: { ...this.$route.query, selections } })
    },
  },
  mounted() {
    this.bus.$on('inquiry', payload => this.$emit('inquiry', payload))
  },
  components: {
    Loader,
    Steps,
    Subcategories,
    ProductDetails,
    ProductModal,
    Summary,
    Sets,
    Intro,
    CostBreakdown,
    Navigation,
  },
}
</script>

<style lang="scss">
@import './mixins';

.shop {
  &--theme-light {
    $base: white;
    @include generateHSL('shop-text-color', #222222);
    @include generateHSL('shop-background-color', $base);
    @include generateHSL('shop-background-color-dark', darken($base, 1.5%));
    @include generateHSL('shop-background-color-darker', darken($base, 5%));
    @include generateHSL('shop-background-color-darkest', darken($base, 9%));
    @include generateHSL('shop-overlay-background-color', darken($base, 5%));
    @include generateHSL('shop-header-background', darken($base, 4%));

    --shop-overlay-alpha: 0.6;
  }

  &--theme-dark {
    $base: #0c1217;
    @include generateHSL('shop-text-color', white);
    @include generateHSL('shop-background-color', $base);
    @include generateHSL('shop-background-color-dark', darken($base, 1.5%));
    @include generateHSL('shop-background-color-darker', darken($base, 5%));
    @include generateHSL('shop-background-color-darkest', darken($base, 9%));
    @include generateHSL('shop-overlay-background-color', darken($base, 5%));
    @include generateHSL('shop-header-background', darken($base, 4%));

    --shop-overlay-alpha: 0.6;

    --border-color: #{lighten($base, 10%)};
  }

  --shop-spacing: 2rem;
  --shop-header-height: 90px;
  --shop-sidebar-width: 500px;
  --shop-responsive-breakpoint: 1000px;

  color: var(--shop-text-color);
  background-color: var(--shop-background-color);

  .elder-button,
  [data-cta] {
    border-radius: calc(var(--border-radius) * 1.5);
  }

  &[data-step='intro'] {
    display: flex;
    flex-direction: column;

    .shop-steps,
    .shop-costbreakdown,
    .shop-navigation {
      display: none;
    }
  }

  &[data-step='sets'] {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;

    grid-template-areas:
      'steps'
      'sets'
      'sets';

    .shop-steps {
      background-color: var(--shop-header-background);
      position: sticky;
      top: 0;
      z-index: 2;
      padding-inline: var(--shop-spacing);
      height: var(--shop-header-height);
    }

    .shop-sets {
      padding-inline: var(--shop-spacing);
      padding-block: var(--shop-spacing);
    }

    .shop-navigation,
    .shop-costbreakdown {
      display: none;
    }
  }

  &[data-step='summary'] {
    .shop-steps {
      background-color: var(--shop-header-background);
      position: sticky;
      top: 0;
      z-index: 2;
      padding-inline: var(--shop-spacing);
      height: var(--shop-header-height);
    }

    & > .shop-costbreakdown {
      display: none;
    }

    .shop-steps,
    .shop-navigation {
      display: none;
    }
  }

  &[data-step='category'] {
    $breakpoint: 1000px;

    display: grid;
    grid-template-columns: 1fr var(--shop-sidebar-width);
    grid-template-rows: auto 1fr auto;

    grid-template-areas:
      'steps steps'
      'product-details subcategories'
      'product-details costbreakdown'
      'product-details navigation';

    @include onPhone {
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr auto auto;
      grid-template-areas: 'steps' 'subcategories' 'costbreakdown' 'navigation';

      & > .shop-product-details {
        display: none;
      }

      .shop-subcategory__item-modal-trigger {
        display: inline-flex;
      }

      .shop-costbreakdown,
      .shop-navigation {
        width: 100% !important;
      }
    }

    .shop-steps {
      position: sticky;
      z-index: 1;
      top: 0;
    }

    .shop-costbreakdown {
      --spacing: 0.15rem;

      position: fixed;
      right: 0;
      bottom: 60px;
      z-index: 1;
      width: var(--shop-sidebar-width);
      padding-inline: var(--shop-spacing);
      padding-block-end: var(--shop-spacing);

      [data-part='total'] {
        & > div:last-child {
          text-decoration: none;
        }
      }
    }

    .shop-navigation {
      position: fixed;
      right: 0;
      bottom: 0;
      z-index: 1;
      width: var(--shop-sidebar-width);

      padding-inline: var(--shop-spacing);
      padding-block-end: var(--shop-spacing);

      background-color: var(--shop-background-color);

      & > * {
        flex-grow: 1;
      }
    }

    .shop-product-details {
      padding: var(--shop-spacing);
    }

    .shop-subcategories {
      --footer-height: 120px;
      --footer-fade: 100px;

      padding-inline: var(--shop-spacing);
      padding-block-start: var(--shop-spacing);
      padding-block-end: calc(var(--footer-height) + var(--footer-fade));

      max-height: calc(100dvh - var(--shop-header-height));
      overflow-y: auto;

      mask-image: linear-gradient(
        to bottom,
        transparent 0%,
        black calc(0% + var(--shop-spacing) * 1.5),
        black calc(100% - var(--footer-height) - var(--footer-fade)),
        transparent calc(100% - var(--footer-height))
      );
    }

    .shop-steps {
      background-color: var(--shop-header-background);
      padding-inline: var(--shop-spacing);
      height: var(--shop-header-height);
    }
  }

  .shop-header {
    display: flex;

    .shop-steps {
      flex-grow: 1;
    }
  }

  .alert {
    a {
      color: inherit;
    }

    &--success .alert__content {
      color: hsl(var(--success-h), var(--success-s), 20%);
    }
  }

  .alert__content:before {
    border-radius: var(--border-radius);
  }
}

$components: 'intro', 'summary', 'product-details', 'costbreakdown', 'navigation', 'subcategories',
  'steps', 'header';

@each $component in $components {
  .shop-#{$component} {
    grid-area: #{$component};
  }
}
</style>
